import { useSyncExternalStore } from "react";
import { installPlatform } from "./install-policy.js";

interface InstallPrompt extends Event {
  prompt(): Promise<{ outcome: "accepted" | "dismissed" }>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}
const key = "fairway-rivalry:install:v1";
const listeners = new Set<() => void>();
let prompt: InstallPrompt | null = null;
let started = false;
let state = {
  available: false,
  installed: false,
  standalone: false,
  dismissedAt: 0,
};

function publish(patch: Partial<typeof state>, persist = false) {
  state = { ...state, ...patch };
  if (persist) {
    try {
      localStorage.setItem(
        key,
        JSON.stringify({
          installed: state.installed,
          dismissedAt: state.dismissedAt,
        }),
      );
    } catch {
      /* Private storage still keeps this visit's preference. */
    }
  }
  listeners.forEach((listener) => listener());
}

export function initializeInstallDiscovery(): void {
  if (started) return;
  started = true;
  try {
    const saved = JSON.parse(localStorage.getItem(key) ?? "null") as {
      installed?: boolean;
      dismissedAt?: number;
    } | null;
    state = {
      ...state,
      installed: saved?.installed === true,
      dismissedAt:
        typeof saved?.dismissedAt === "number" &&
        Number.isFinite(saved.dismissedAt)
          ? saved.dismissedAt
          : 0,
    };
  } catch {
    /* Storage is optional. */
  }
  const display = window.matchMedia("(display-mode: standalone)");
  const refreshDisplay = () => {
    const standalone =
      display.matches ||
      (navigator as Navigator & { standalone?: boolean }).standalone === true;
    publish({ standalone });
    if (standalone) markAppInstalled();
  };
  refreshDisplay();
  display.addEventListener("change", refreshDisplay);
  window.addEventListener("pageshow", refreshDisplay);
  window.addEventListener("appinstalled", markAppInstalled);
  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault();
    prompt = event as InstallPrompt;
    publish({ available: true });
  });
}

export function markAppInstalled(): void {
  prompt = null;
  publish({ installed: true, available: false }, true);
}

export function dismissInstallInvitation(): void {
  publish({ dismissedAt: Date.now() }, true);
}

/** Invoke directly from a click: the browser requires transient user activation. */
export async function requestAppInstall(): Promise<
  "accepted" | "dismissed" | "unavailable"
> {
  const event = prompt;
  if (!event) return "unavailable";
  prompt = null;
  publish({ available: false });
  try {
    const result = await event.prompt();
    const choice = result ?? (await event.userChoice);
    if (choice.outcome === "accepted") markAppInstalled();
    else dismissInstallInvitation();
    return choice.outcome;
  } catch {
    dismissInstallInvitation();
    return "unavailable";
  }
}

export function openInstallHelp(): void {
  window.dispatchEvent(new Event("fairway:install-help"));
}

export function useInstallState() {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    () => state,
  );
}

export function currentInstallPlatform() {
  return installPlatform(navigator.userAgent, navigator.maxTouchPoints);
}
